import { DatePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { map, take } from 'rxjs';
import {
  IMAGE_SRC,
  VALIDATE_ADDRESS_RESPONSES,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';

import { nonCanadianPostalValidator } from 'src/app/core/custom-validators/non-canadian-postal/non-canadian-postal.validator';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import { ValidateAddressRequest } from 'src/app/core/models/operations/operations-pickup.model';
import { Days } from 'src/app/core/models/operations/operations-scheduled-pickup.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OperationsUtils } from 'src/app/core/utils/operations/operations.utils';
import {
  CONSTANTS,
  PICKUPS,
} from '../../pickup-assignment/pickup/scheduled-pickup/scheduled-pickup.constants';

@Component({
  selector: 'app-pickup-popup',
  templateUrl: './pickup-popup.component.html',
  styleUrls: ['./pickup-popup.component.scss'],
  standalone: false,
})
export class PickupPopupComponent implements OnInit {
  routes = input.required<string[]>();
  formInput = input.required<FormGroup | undefined>();
  countryCode = input<string>('');
  submitFormEvent = output<typeof this.pickupForm.value>();

  public commonUtils = inject(CommonUtils);
  private readonly fb = inject(FormBuilder);
  private readonly pickupAssignmentsService = inject(PickupAssignmentsService);
  private readonly datePipe = inject(DatePipe);
  private readonly operationsUtils = inject(OperationsUtils);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);

  readonly SpecialCharacters = SpecialCharacters;
  readonly PICKUPS = PICKUPS;
  readonly CONSTS = {
    ...CONSTANTS,
    ...VALIDATION_MESSAGE,
    ...VALIDATE_ADDRESS_RESPONSES,
  };
  readonly CONTROLS = PICKUPS;
  readonly SRC = IMAGE_SRC;
  readonly destroy = inject(DestroyRef);

  currentUser!: UserAdmin;
  userId!: string;
  daysAssigned!: Days;
  pickupForm!: FormGroup;

  isIDO = signal<boolean>(false);

  defaultStartTime = '';
  defaultEndTime = '';
  validationCode = '';
  validatedLatNr = '';
  validatedLngNr = '';
  daysAvailable: boolean[] = [];
  isOnDemandPickup = false;
  minDate: Date = new Date();
  maxDate: Date = new Date(
    this.minDate.getFullYear(),
    this.minDate.getMonth(),
    this.minDate.getDate() + 14,
  );
  scheduledMaxDate: Date = new Date(this.minDate.getFullYear() + 10, 11, 31);
  scheduledMaxEndDate: Date = new Date(2099, 11, 31);

  ngOnInit() {
    this.routes.apply(
      this.routes().filter(this.operationsUtils.getUniqueValues),
    );

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.currentUser = user;
          this.userId = user.loginId;
          this.initializeForm();
          this.createDaysAssigned();
          this.subscribeToOnDemandPickup();
          this.subscribeToDaysAssigned();
          this.getIDOStatus();
        }),
      )
      .subscribe();

    this.isOnDemandPickup =
      this.pickupForm?.get(PICKUPS.ON_DEMAND_PICKUP)?.value || false;

    this.commonUtils.autoCapitalize(
      this.pickupForm,
      [this.CONTROLS.PICKUP_POINT],
      this.destroy,
    );
  }

  initializeForm() {
    const isValidatorOptional = this.commonUtils.isStateProvinceOptional(
      this.countryCode(),
      this.currentUser.locationGroup,
      this.currentUser.location,
    );

    this.pickupForm = this.fb.group({
      [PICKUPS.ON_DEMAND_PICKUP]: [false],
      [PICKUPS.DAYS_ASSIGNED]: [this.daysAssigned, Validators.required],
      [PICKUPS.PICKUP_REQUEST_NUMBER]: [
        { value: '', disabled: !this.isOnDemandPickup },
      ],
      [PICKUPS.PICKUP_POINT]: [''],
      [PICKUPS.ROUTE]: [{ value: '', disabled: this.isOnDemandPickup }],
      [PICKUPS.START_DATE]: new Date(),
      [PICKUPS.END_DATE]: [this.isOnDemandPickup ? '' : new Date('12-31-2099')],
      [PICKUPS.READY_TIME]: [
        this.datePipe.transform(new Date(), 'HH:mm'),
        Validators.compose([Validators.required, Validators.minLength(5)]),
      ],
      [PICKUPS.COMMIT_TIME]: [
        this.datePipe.transform(new Date(), 'HH:mm'),
        Validators.compose([Validators.required, Validators.minLength(5)]),
      ],
      [PICKUPS.CLOSING_TIME]: [
        this.datePipe.transform(new Date(), 'HH:mm'),
        Validators.compose([Validators.required, Validators.minLength(5)]),
      ],
      [PICKUPS.INTERRUPT_DATE]: [
        { value: '', disabled: this.isOnDemandPickup },
      ],
      [PICKUPS.RESTART_DATE]: [{ value: '', disabled: this.isOnDemandPickup }],
      [PICKUPS.SHIPPER_NUMBER]: ['', Validators.required],
      [PICKUPS.CUSTOMER_NAME]: ['', Validators.required],
      [PICKUPS.CONTACT_NAME]: [''],
      [PICKUPS.CONTACT_NUMBER]: [''],
      [PICKUPS.STREET_ADDRESS]: [''],
      [PICKUPS.PREFIX]: [''],
      [PICKUPS.STREET_NAME]: ['', Validators.required],
      [PICKUPS.SUFFIX]: [''],
      [PICKUPS.SUITE]: [''],
      [PICKUPS.FLOOR]: [''],
      [PICKUPS.CITY]: [''],
      [PICKUPS.PROVINCE]: [
        '',
        isValidatorOptional ? Validators.nullValidator : Validators.required,
      ],
      [PICKUPS.POSTAL_CODE]: [
        '',
        Validators.compose([
          Validators.required,
          nonCanadianPostalValidator(this.countryCode()),
        ]),
      ],
      [PICKUPS.LATITUDE]: [''],
      [PICKUPS.LONGITUDE]: [''],
      [PICKUPS.SSC_DESCRIPTION]: [{ value: '', disabled: true }],
      [PICKUPS.ADDITIONAL_INFORMATION]: [''],
    });
  }

  subscribeToOnDemandPickup() {
    this.pickupForm
      ?.get(PICKUPS.ON_DEMAND_PICKUP)
      ?.valueChanges.subscribe((isOnDemandPickup) => {
        if (isOnDemandPickup) {
          this.isOnDemandPickup = true;
          this.pickupForm.get(PICKUPS.PICKUP_REQUEST_NUMBER)?.enable();
          this.pickupForm.get(PICKUPS.ROUTE)?.disable();
          //Reset values to default
          this.pickupForm.get(PICKUPS.DAYS_ASSIGNED)?.patchValue({
            friday: false,
            monday: false,
            saturday: false,
            sunday: false,
            thursday: false,
            tuesday: false,
            wednesday: false,
          });
          this.pickupForm.get(PICKUPS.DAYS_ASSIGNED)?.disable();
        } else {
          this.isOnDemandPickup = false;
          this.pickupForm.get(PICKUPS.PICKUP_REQUEST_NUMBER)?.disable();
          this.pickupForm.get(PICKUPS.ROUTE)?.enable();
          this.pickupForm.get(PICKUPS.DAYS_ASSIGNED)?.enable();
        }
        this.pickupForm.updateValueAndValidity();
      });
  }

  subscribeToDaysAssigned() {
    this.pickupForm
      .get(PICKUPS.DAYS_ASSIGNED)
      ?.valueChanges.subscribe((days: Days) => {
        if (
          !days?.sunday &&
          !days?.monday &&
          !days?.tuesday &&
          !days?.wednesday &&
          !days?.thursday &&
          !days?.friday &&
          !days?.saturday
        ) {
          this.pickupForm.controls[PICKUPS.DAYS_ASSIGNED]?.setErrors({
            required: true,
          });
        }
      });
  }

  onSubmit() {
    this.submitFormEvent.emit(this.pickupForm.getRawValue());
  }

  validate() {
    const validateAddressRequest: ValidateAddressRequest = {
      addressValidation: {
        address: {
          consignee: this.pickupForm.get(this.CONTROLS.CUSTOMER_NAME)?.value,
          city: this.pickupForm.get(this.CONTROLS.CITY)?.value,
          state: this.pickupForm.get(this.CONTROLS.PROVINCE)?.value,
          postalCode: this.pickupForm.get(this.CONTROLS.POSTAL_CODE)?.value,
          country: this.countryCode(),
          floor: this.pickupForm.get(this.CONTROLS.FLOOR)?.value,
          suite: this.pickupForm.get(this.CONTROLS.SUITE)?.value,
          streetParsed: {
            number: this.pickupForm.get(this.CONTROLS.STREET_ADDRESS)?.value,
            name: this.pickupForm.get(this.CONTROLS.STREET_NAME)?.value,
            prefix: this.pickupForm.get(this.CONTROLS.PREFIX)?.value,
            suffix: this.pickupForm.get(this.CONTROLS.SUFFIX)?.value,
            type: '',
          },
        },
        userId: this.userId,
        orderNumber: '',
      },
    };
    this.pickupAssignmentsService
      .validateAddress(validateAddressRequest, this.currentUser.location)
      .pipe(take(1))
      .subscribe((data) => {
        this.validationCode = data.status;
        if (data.status === '0' && data.address) {
          this.validatedLatNr = data.address.latNr;
          this.validatedLngNr = data.address.lngNr;
          this.pickupForm.get(PICKUPS.LATITUDE)?.setValue(this.validatedLatNr);
          this.pickupForm.get(PICKUPS.LONGITUDE)?.setValue(this.validatedLngNr);
        }
      });
  }

  createDaysAssigned() {
    this.daysAssigned = {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
    };
  }

  clickExit() {
    this.commonUtils.openExitModal(this.pickupForm.dirty);
  }

  getIDOStatus() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((profile) => {
        this.isIDO.set(profile.location?.routeDeliveryOrder ?? false);
      });
  }

  daysAssignedUpdated(event: Days) {
    this.pickupForm.get(PICKUPS.DAYS_ASSIGNED)?.setValue(event);
    this.pickupForm.markAsDirty();
  }
}
